import { useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"
import { Grid } from "@mui/material"
import { alpha, useTheme } from "@mui/system"
import EditIcon from "@mui/icons-material/Edit"

import type { LatLonRecord } from "types/geolocation.types"

import {
  useDeleteLocationMutation,
  useLastLocationsQuery,
  useUpdateLocationMutation,
} from "features/api"
import { MAP_HEIGHT_COMPACT } from "helpers/utils/constants"
import ConfirmationPopup from "widgets/common/ConfirmationPopup"
import GeoSettingsPlot from "widgets/plots/geo/GeoSettingsPlot"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import { toSentenceCase } from "helpers/utils/translations"
import CardWithTitle from "widgets/common/CardWithTitle"
import usePermissions from "helpers/hooks/usePermissions"
import { getAndFormatLastLatLon } from "helpers/utils/map"
import { GeolocationInput } from "./GeolocationInput"

export interface DeviceLocationForm {
  lat: string
  lon: string
}

interface DeviceLocationProps {
  deviceId: number
}

function DeviceLocation({ deviceId }: DeviceLocationProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const { isEditor } = usePermissions()

  const initialCoords: DeviceLocationForm = useMemo(() => {
    return { lat: "", lon: "" }
  }, [])

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false)
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false)
  const [coords, setCoords] = useState<DeviceLocationForm | null>(initialCoords)
  const [lastLocation, setLastLocation] = useState<LatLonRecord[] | null>([])
  const [updateLocation, { isLoading: isLoadingUpdate }] = useUpdateLocationMutation()
  const [deleteLocation, { isLoading: isLoadingDelete }] = useDeleteLocationMutation()

  const { currentData: currentLocationData, isFetching: isFetchingQuery } =
    useLastLocationsQuery({ params: { less_id: deviceId } }, { skip: !deviceId })

  const methods = useForm<DeviceLocationForm>({
    mode: "all",
    defaultValues: {
      lat: coords ? coords.lat : "0",
      lon: coords ? coords.lon : "0",
    },
  })

  const { resetField, setValue, getValues } = methods

  const onSubmit = () => {
    setOpenConfirmationPopup(true)
  }

  const confirmUpdate = () => {
    snackbarMutation({
      mutation: updateLocation({
        deviceId,
        latitude: Number(getValues("lat")),
        longitude: Number(getValues("lon")),
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(t("device_settings.FAILED_LOCATION_UPDATE")),
      getSuccessMessage: () =>
        t("success.UPDATING_ITEM", {
          item: toSentenceCase(t("device_information.DEVICE_LOCATION")),
          count: 1,
          context: "female",
        }),
    })
      .then(() => {
        setIsEditing(false)
        setLastLocation(null)
        setCoords(null)
        setOpenConfirmationPopup(false)
      })
      .catch(() => {})
  }

  const handleDelete = () => setOpenDeletePopup(true)

  const confirmDelete = () => {
    snackbarMutation({
      mutation: deleteLocation({
        deviceId,
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(t("device_settings.FAILED_LOCATION_DELETE")),
      getSuccessMessage: () =>
        t("success.DELETING_ITEM", {
          item: toSentenceCase(t("device_information.DEVICE_LOCATION")),
          count: 1,
          context: "female",
        }),
    })
      .then(() => {
        resetField("lat")
        resetField("lon")
        setIsEditing(false)
        setLastLocation(null)
        setCoords(null)
        setOpenDeletePopup(false)
      })
      .catch(() => {})
  }

  const onEditClick = () => {
    setIsEditing(true)
    if (coords) {
      setValue("lat", coords.lat)
      setValue("lon", coords.lon)
    }
  }
  const onCancelEdit = () => {
    setIsEditing(false)
    resetField("lat")
    resetField("lon")
  }
  const onCloseConfirmationPopup = () => {
    setOpenConfirmationPopup(false)
  }

  const onCloseDeletePopup = () => {
    setOpenDeletePopup(false)
  }

  useEffect(() => {
    const coordsGps = getAndFormatLastLatLon(currentLocationData as LatLonRecord[])

    if (coordsGps && currentLocationData) {
      setLastLocation(currentLocationData as LatLonRecord[])
      setCoords(coordsGps)
    }
  }, [currentLocationData, deviceId])

  return (
    <>
      <CardWithTitle
        titleKey={"device_information.DEVICE_LOCATION"}
        tooltipContent={[t("device_information.COORDINATES_STANDARD")]}
        actionPermission={isEditor}
        isDoingAction={isEditing}
        handleAction={onEditClick}
        actionIcon={<EditIcon fontSize="small" />}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormProvider {...methods}>
            <Grid container>
              <Grid item xs={12} sm={12} md={8} lg={6} xl={4}>
                <GeolocationInput
                  name={"lat"}
                  isEditing={isEditing}
                  disabled={isLoadingUpdate}
                  isWaiting={isFetchingQuery || isLoadingUpdate}
                  coords={coords}
                />
                <GeolocationInput
                  name={"lon"}
                  isEditing={isEditing}
                  disabled={isLoadingUpdate}
                  isWaiting={isFetchingQuery || isLoadingUpdate}
                  coords={coords}
                />
              </Grid>
            </Grid>
            <GeoSettingsPlot
              isLoading={isFetchingQuery || isLoadingUpdate}
              mapHeight={MAP_HEIGHT_COMPACT}
              data={lastLocation || []}
            />
            {isEditing && (
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                flexWrap={"wrap"}
                gap={2}
                mt={2}
              >
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={!lastLocation?.length || isLoadingDelete}
                >
                  {t("generic.DELETE")}
                </Button>
                <Stack direction={"row"} gap={2}>
                  <Button
                    sx={{
                      color: theme.palette.grey[500],
                      borderColor: theme.palette.grey[500],
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.grey[500], 0.1),
                        borderColor: theme.palette.grey[400],
                      },
                    }}
                    variant="outlined"
                    aria-label="cancel"
                    onClick={onCancelEdit}
                    disabled={isLoadingUpdate}
                  >
                    {t("generic.CANCEL")}
                  </Button>
                  <Button type="submit" variant="outlined" disabled={isLoadingUpdate}>
                    {t("generic.UPDATE")}
                  </Button>
                </Stack>
              </Stack>
            )}
          </FormProvider>
        </form>
      </CardWithTitle>
      <ConfirmationPopup
        open={openConfirmationPopup}
        onClose={onCloseConfirmationPopup}
        description={t("device_settings.ARE_YOU_SURE_TO_UPDATE_LOCATION")}
        mainButtonText={t("generic.CONFIRM")}
        onConfirm={confirmUpdate}
        isLoading={isLoadingUpdate}
      />
      <ConfirmationPopup
        open={openDeletePopup}
        onClose={onCloseDeletePopup}
        description={t("device_settings.ARE_YOU_SURE_TO_DELETE_LOCATION")}
        mainButtonText={t("generic.DELETE")}
        onConfirm={confirmDelete}
        isLoading={isLoadingDelete}
        isDeletePopup
      />
    </>
  )
}

export default DeviceLocation
